import { ComponentProps, FC, ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@new/lib';

const buttonVariants = cva(
  `inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md! font-semibold!
    transition-colors cursor-pointer
    disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed
    [&_svg]:pointer-events-none
    [&_svg:not([class*='size-'])]:size-4
    [&_svg]:shrink-0
    shrink-0
    outline-none
    focus-visible:border-ring
    focus-visible:ring-ring/50
    focus-visible:ring-[3px]
    aria-invalid:ring-destructive/20
    dark:aria-invalid:ring-destructive/40
    aria-invalid:border-destructive`,
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90 [&&]:[font-size:var(--fs-l5)]! md:[&&]:[font-size:var(--fs-b4)]!',

        primary: `
          [&&]:[font-family:var(--font-arvo)]!
          bg-(--primary-O900)!
          [&&]:text-(--neutral-white-NW900)!
          hover:bg-(--primary-O800)!
          disabled:bg-(--neutral-white-NW700)!`,

        destructive: `
          bg-destructive
          [&&]:text-(--neutral-white-NW900)!
          hover:bg-destructive/90
          focus-visible:ring-destructive/20
          dark:focus-visible:ring-destructive/40
          dark:bg-destructive/60`,

        outline: `
          border-2!
          border-(--primary-O900)!
          text-(--primary-O900)!
          bg-transparent
          [&&]:[font-family:var(--font-arvo)]!`,

        secondary: `
          bg-(--neutral-white-NW1000) font-bold
          [&&]:text-(--primary-O1100)!
          hover:bg-(--primary-O200)`,

        inverted: `
          bg-transparent
          [&&]:text-(--primary-O900)!
          [&&]:[font-family:var(--font-arvo)]!`,

        neutral: `
          border-2!
          border-(--neutral-white-NW700)!
          bg-transparent
          [&&]:text-(--neutral-white-NW700)!
          hover:[&&]:text-(--neutral-white-NW600)!
          hover:border-(--neutral-white-NW600)!
          [&&]:[font-family:var(--font-arvo)]!`,

        ghost: `
          bg-transparent
          hover:bg-accent
          hover:text-accent-foreground
          dark:hover:bg-accent/50`,

        link: `
          bg-transparent
          text-primary
          underline-offset-4
          hover:underline`,
      },

      size: {
        default: 'h-9 md:h-10 px-3 py-2 has-[>svg]:px-3',
        sm: 'h-8 gap-1.5 px-3 has-[>svg]:px-2.5',
        xs: 'h-6 gap-1 px-2 has-[>svg]:px-1.5',
        lg: 'h-10 px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },

    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    text?: string;
  };

export const Button: FC<ButtonProps> = ({
  className,
  variant = 'primary',
  size,
  asChild = false,
  leftIcon,
  rightIcon,
  text,
  children,
  ...props
}) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {leftIcon}
      {text ?? children}
      {rightIcon}
    </Comp>
  );
};
